import type { CalendarEvent } from 'src/meetings/models';

type ClientGuest = {
  name: string;
  participantType: 'CLIENT';
  clientId: string;
};

type UserGuest = {
  name: string;
  participantType: 'USER';
  userId: string;
};

type GuestDisplayGuest = ClientGuest | UserGuest;

type GuestDisplayData = {
  totalGuests: number;
  knownGuests: GuestDisplayGuest[];
  unknownCount: number;
};

const buildFullName = (firstName?: string | null, lastName?: string | null): string | undefined => {
  const parts = [firstName, lastName].filter(
    (part): part is string => typeof part === 'string' && part.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(' ') : undefined;
};

export const getGuestDisplayData = (event: CalendarEvent): GuestDisplayData => {
  const knownGuests = event.eventParticipants.flatMap<GuestDisplayGuest>(participant => {
    const { client, user } = participant.participant;

    if (client) {
      const name = buildFullName(client.firstName, client.lastName);
      return name
        ? [
            {
              name,
              participantType: 'CLIENT',
              clientId: client.id,
            },
          ]
        : [];
    }

    if (user) {
      const name = buildFullName(user.firstName, user.lastName);
      return name
        ? [
            {
              name,
              participantType: 'USER',
              userId: user.id,
            },
          ]
        : [];
    }

    return [];
  });

  const totalGuests = event.eventParticipants.length;
  const unknownCount = Math.max(totalGuests - knownGuests.length, 0);

  return {
    totalGuests,
    knownGuests,
    unknownCount,
  };
};

export const getFirstClientParticipant = (
  event: CalendarEvent,
): CalendarEvent['eventParticipants'][0] | undefined => {
  return event.eventParticipants.find(p => p.participant.client !== null);
};

export const getAllClientParticipants = (
  event: CalendarEvent,
): CalendarEvent['eventParticipants'] => {
  return event.eventParticipants.filter(p => p.participant.client !== null);
};

export const getClientParticipantIds = (event: CalendarEvent): string[] =>
  getAllClientParticipants(event)
    .map(eventParticipant => eventParticipant.participant.client?.id)
    .filter((clientId): clientId is string => !!clientId);

export type ParticipantFamily = { id: string; name: string };

// The single family every given client belongs to, or undefined when they span
// different families or any of them has no family. A lone client who is part of
// a family counts as sharing it.
export function getSharedFamily(
  clientIds: string[],
  familiesByClientId: Map<string, ParticipantFamily>,
): ParticipantFamily | undefined {
  const families = clientIds
    .map(clientId => familiesByClientId.get(clientId))
    .filter((family): family is ParticipantFamily => !!family);
  if (families.length === 0 || families.length !== clientIds.length) return undefined;
  const distinctFamilyIds = new Set(families.map(family => family.id));
  return distinctFamilyIds.size === 1 ? families[0] : undefined;
}

/**
 * How many of a meeting's client attendees belong to each family. A family with
 * two or more members present is the threshold for collapsing those members into
 * a single "<name> family" label; a lone member is shown by name.
 */
const countClientFamilyMembers = (
  participants: CalendarEvent['eventParticipants'],
  familiesByClientId?: Map<string, ParticipantFamily>,
): Map<string, number> => {
  const counts = new Map<string, number>();
  if (!familiesByClientId) return counts;
  for (const { participant } of participants) {
    const family = participant.client && familiesByClientId.get(participant.client.id);
    if (family) {
      counts.set(family.id, (counts.get(family.id) ?? 0) + 1);
    }
  }
  return counts;
};

type MeetingClient = NonNullable<
  CalendarEvent['eventParticipants'][number]['participant']['client']
>;

type CollapsedClient =
  | { kind: 'family'; family: ParticipantFamily; client: MeetingClient }
  | { kind: 'client'; client: MeetingClient };

/**
 * Builds a classifier over a meeting's client attendees that collapses each family
 * with two or more members present into a single entry at its first appearance;
 * later members of an already-emitted family classify as null (skip). Shared by the
 * attendee-pill, hub-label and recording-target builders so the collapse rule can't
 * drift apart between them.
 */
const makeClientFamilyCollapser = (
  participants: CalendarEvent['eventParticipants'],
  familiesByClientId?: Map<string, ParticipantFamily>,
): ((client: MeetingClient) => CollapsedClient | null) => {
  const familyMemberCounts = countClientFamilyMembers(participants, familiesByClientId);
  const emittedFamilyIds = new Set<string>();
  return client => {
    const family = familiesByClientId?.get(client.id);
    if (family && (familyMemberCounts.get(family.id) ?? 0) >= 2) {
      if (emittedFamilyIds.has(family.id)) return null;
      emittedFamilyIds.add(family.id);
      return { kind: 'family', family, client };
    }
    return { kind: 'client', client };
  };
};

/**
 * Display names for a meeting's attendees — advisers (user participants),
 * clients, and external attendees (those added by name).
 *
 * When `familiesByClientId` is supplied and two or more client attendees belong
 * to the same family, that group collapses to a single "<name> family" label
 * (placed where the family's first attendee appears) instead of listing each
 * member individually.
 */
export const getParticipantDisplayNames = (
  event: CalendarEvent,
  familiesByClientId?: Map<string, ParticipantFamily>,
): string[] => {
  const collapse = makeClientFamilyCollapser(event.eventParticipants, familiesByClientId);
  const names: string[] = [];

  for (const { participant } of event.eventParticipants) {
    if (participant.client) {
      const entry = collapse(participant.client);
      if (!entry) continue;
      const name =
        entry.kind === 'family'
          ? `${entry.family.name} family`
          : buildFullName(entry.client.firstName, entry.client.lastName);
      if (name) names.push(name);
      continue;
    }
    if (participant.user) {
      const name = buildFullName(participant.user.firstName, participant.user.lastName);
      if (name) names.push(name);
      continue;
    }
    if (participant.participantType === 'EXTERNAL' && participant.name) {
      names.push(participant.name);
    }
  }

  return names;
};

export type RecordingDataTarget =
  | { kind: 'client'; clientId: string; name: string }
  | { kind: 'family'; familyId: string; reviewClientId: string; name: string };

/**
 * The distinct "records" a meeting's recording can be processed into: one per family with two or
 * more members present (a shared family review, resolved when all members are done) and one per
 * remaining client. A lone family member is treated as an individual, matching how attendees are
 * displayed — the absent members aren't part of this meeting's recording.
 *
 * Pass `familiesByClientId` only when family review is available; without it every client attendee
 * becomes an individual target.
 */
export const getRecordingDataTargets = (
  event: CalendarEvent,
  familiesByClientId?: Map<string, ParticipantFamily>,
): RecordingDataTarget[] => {
  const clientParticipants = getAllClientParticipants(event);
  const collapse = makeClientFamilyCollapser(clientParticipants, familiesByClientId);
  const targets: RecordingDataTarget[] = [];

  for (const { participant } of clientParticipants) {
    if (!participant.client) continue;
    const entry = collapse(participant.client);
    if (!entry) continue;

    if (entry.kind === 'family') {
      targets.push({
        kind: 'family',
        familyId: entry.family.id,
        reviewClientId: entry.client.id,
        name: `${entry.family.name} family`,
      });
      continue;
    }

    const client = entry.client;
    targets.push({
      kind: 'client',
      clientId: client.id,
      name:
        buildFullName(client.firstName, client.lastName) || (client.emailAddress ?? 'this client'),
    });
  }

  return targets;
};

/**
 * Names of the clients linked to a meeting. Used on the practice-wide hub to
 * show which client(s) a meeting belongs to, since the list spans everyone.
 *
 * When `familiesByClientId` is supplied and two or more client attendees belong
 * to the same family, that group collapses to a single "<name> family" label
 * (placed where the family's first attendee appears), matching how the attendee
 * pill labels the same meeting.
 */
export const getClientDisplayNames = (
  event: CalendarEvent,
  familiesByClientId?: Map<string, ParticipantFamily>,
): string[] => {
  const clientParticipants = getAllClientParticipants(event);
  const collapse = makeClientFamilyCollapser(clientParticipants, familiesByClientId);
  const names: string[] = [];

  for (const { participant } of clientParticipants) {
    if (!participant.client) continue;
    const entry = collapse(participant.client);
    if (!entry) continue;
    const name =
      entry.kind === 'family'
        ? `${entry.family.name} family`
        : buildFullName(entry.client.firstName, entry.client.lastName);
    if (name) names.push(name);
  }

  return names;
};

export const getParticipantFullName = (
  participant: CalendarEvent['eventParticipants'][0] | undefined,
): string | undefined => {
  if (!participant) {
    return undefined;
  }

  const { client, user } = participant.participant;

  if (client) {
    return buildFullName(client.firstName, client.lastName);
  }

  if (user) {
    return buildFullName(user.firstName, user.lastName);
  }

  return undefined;
};
